import { OAuth2 } from '@backstage/core-app-api';
import {
  BackstageIdentityApi,
  ConfigApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  configApiRef,
  createApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/frontend-plugin-api';

/**
 * Sign in by redirecting the whole page to Keycloak instead of opening a popup. The flag is read by the auth
 * connector from the config API, but it is not part of the config exposed to the browser, so it is forced here.
 */
const withRedirectFlow = (configApi: ConfigApi): ConfigApi =>
  new Proxy(configApi, {
    get(target, property) {
      if (property === 'getOptionalBoolean')
        return (key: string) =>
          key === 'enableExperimentalRedirectFlow'
            ? true
            : target.getOptionalBoolean(key);
      const value = Reflect.get(target, property);
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });

export const keycloakAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>().with({
  id: 'auth.keycloak',
});

export const keycloakAuthApi = ApiBlueprint.make({
  name: 'keycloak',
  params: defineParams =>
    defineParams({
      api: keycloakAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi: withRedirectFlow(configApi),
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
          provider: {
            id: 'keycloak',
            title: 'Keycloak',
            icon: () => null,
          },
          defaultScopes: ['openid', 'profile', 'email'],
        }),
    }),
});
